export default {
  en: {
    blueprint: 'Blueprint',
    branches: 'Branches',
    aboutBranch: {
      title: 'About this branch.',
      content: 'The branch you select is where Cycloid will discover stacks in the repository. Once the catalog repo is created, you can create additional branches to test new stack versions.',
    },
    catalogStatus: {
      allHint: 'Both public and private stacks',
      ownHint: `All your organization's stacks`,
      ownName: `Organization stacks`,
    },
    cloneInstructions: 'Clone the catalog repository on your local machine to copy or edit stacks. Then commit and push your changes to your target catalog repository.',
    gitRepo: 'Git repository',
    gitRepoHint: 'Connect an existing git repository.',
    changeStackVersion: 'Change stack version',
    clone: 'Clone',
    commit: 'Commit',
    deleted: 'deleted',
    incompatible: {
      tag: 'Incompatible',
      title: 'Incompatible stack',
      alreadyUsingV3: 'This environment already has a stack using <b>version 3 format</b>, there can be only one stack using format version 1-3 in the same environment.',
      migrateToV4: `To add this stack, you'll need to migrate it to <b>version 4</b>.`,
      docsLink: 'View the migration guide to stack format v4',
    },
    latest: 'latest',
    noStacksFound: {
      title: 'No stacks found',
      subtitle: 'Let’s get started by creating a stack in this repository. Click the <strong>Create stack</strong> button or push code to the Git repository to add stacks manually.',
    },
    maintainer: {
      description: 'Designate a team as stack maintainer. Note: This attribute serves for informational purposes only.',
      label: 'Maintainer',
      noMaintainer: 'No maintainer',
      assignMaintainer: 'Assign maintainer',
    },
    tags: '@:untranslated.tags',
    stackActions: 'Stack Actions',
    version: {
      title: 'Version',
      branchBasedVersioning: 'Branch-based versioning.',
      branchBasedDescription: 'Use branches to test changes in a development environment before publishing a stable version.',
      currentVersion: 'You are currently using version <i>{versionName}</i>',
      updateAvailable: 'Update available.',
      updateAvailableDescription: 'This component was provisioned using stack version {currentVersion}. A new stack version {newVersion} is now available.',
      updateDeprecatedVersion: 'Update deprecated version.',
      updateDeprecatedDescription: 'This component was provisioned using stack version {currentVersion} which is marked as deprecated. A new stack version {newVersion} is now available.',
      newVersionAvailable: 'A new stack version is available',
      reviewUpdate: 'Review update',
      selectVersionText: 'Select the version of the stack that will be used to provision this component.',
      stackVersion: 'Stack version',
      targetVersion: 'Target version',
      upToDate: {
        title: 'Component up-to-date',
        branchText: 'This component uses the {versionName} branch on commit {commitHash} of {stackCanonical}.',
        tagText: 'This component uses the {versionName} tag on commit {commitHash} of {stackCanonical}.',
      },
      deletedVersionWarning: 'The version this component was using is no longer available. Please review the configuration and select a new version before saving.',
      refreshVersions: 'Refresh versions',
      refreshVersionsSharedStackTooltip: 'This stack belongs to another organization. Only they can refresh versions.',
      changeVersion: 'Change version',
      outOfSyncBranch: {
        title: 'Component out-of-sync',
        text: 'This component does not match the latest commit of the {versionName} branch of {stackCanonical}.',
        current: 'Current:',
        latest: 'Latest:',
        instructions: 'Review and apply the update to bring the component back in sync.',
      },
      outOfSyncTag: {
        title: 'Component out-of-sync',
        text: 'This component does not match the commit currently referenced by the {versionName} tag of {stackCanonical}.',
        current: 'Current:',
        referencedCommit: 'Referenced commit:',
        instructions: 'Review and apply the update to bring the component back in sync.',
      },
      newStackVersionAvailable: {
        title: 'New stack version available',
        text: '{stackCanonical} latest version is {latestVersion}, you are using {currentVersion}.',
      },
      deleted: {
        title: 'Stack version deleted',
        text: 'The stack version {currentVersion} used by this component no longer exists in {stackCanonical}.',
        instructions: 'Update this component and select a valid version.',
      },
    },
    useCase: {
      placeholder: 'Select a use-case',
      description: 'Each use-case exposes a different configuration.',
      hint: 'Select a use-case to configure this component',
      info: 'Defines the functional variant of the stack. This cannot be changed after creation.',
      originalUseCaseUnavailableWithTeam: '<b>Original use case unavailable.</b> The configured use case <i>{usecase}</i> does not exist in the selected stack version. Please contact the <i>{team}</i> team (stack maintainers) to resolve the incompatibility.',
      originalUseCaseUnavailableWithAdmin: '<b>Original use case unavailable.</b> The configured use case <i>{usecase}</i> does not exist in the selected stack version. Please contact an administrator to resolve the incompatibility.',
    },
    visibility: {
      title: 'Access and accountability',
      description: 'Choose a visibility and assign maintainers to stacks  already present in the git repository. You can always change those later on for each stack.',
      label: 'Stack visibility',
      local: 'Local',
      localHint: 'Only accessible to users within this organization.',
      shared: 'Shared',
      sharedHint: 'Accessible to users within this organization and its child organizations.',
      sharedByParentOrgHint: 'Shared by a parent organization. Accessible to users within this organization and all its child organizations.',
      hidden: 'Hidden',
      hiddenHint: 'Not displayed in the stack list or during project creation. Users within this organization can still use it by manually revealing hidden stacks.',
      changeVisibility: 'Change visibility',
      set: 'Set visibility',
    },
    outdated: 'outdated',
    outOfSync: {
      text: 'out-of-sync',
      title: 'This component is out-of-sync',
      description: 'The commit for <b>{stackName} {version}</b> has changed, likely due to an update in the associated tag or branch.',
      instructions: 'Update this component to align it with the latest commit.',
      action: 'Review update',
    },
  },
  es: {
    blueprint: 'Plantilla',
    branches: 'Ramas',
    aboutBranch: {
      title: 'Sobre esta rama.',
      content: 'La rama que selecciones es donde Cycloid descubrirá los stacks en el repositorio. Una vez creado el repositorio de catálogo, puedes crear ramas adicionales para probar nuevas versiones de stacks.',
    },
    catalogStatus: {
      allHint: 'Stacks públicos y privados',
      ownHint: 'Todos los stacks de tu organizacion',
      ownName: 'Organizacion stacks',
    },
    cloneInstructions: 'Clone el repositorio del catálogo en su máquina local para copiar o editar stacks. Luego confirme y envíe sus cambios a su repositorio de catálogo de destino.',
    gitRepo: 'Repositorio git',
    gitRepoHint: 'Conectar un repositorio Git existente.',
    changeStackVersion: 'Cambiar versión del stack',
    clone: 'Clonar',
    commit: 'Commit',
    deleted: 'deleted',
    incompatible: {
      tag: 'Incompatible',
      title: 'Stack incompatible',
      alreadyUsingV3: 'Este entorno ya tiene un stack que utiliza el <b>formato de la versión 3</b>, solo puede haber un stack que utilice el formato de la versión 1-3 en el mismo entorno.',
      migrateToV4: 'Para agregar este stack, deberá migrarlo a la <b>versión 4</b>.',
      docsLink: 'Ver la guía de migración al formato de stack v4',
    },
    latest: 'último',
    noStacksFound: {
      title: 'No se encontraron stacks',
      subtitle: 'Comencemos creando un stack en este repositorio. Haz clic en el botón <strong>Crear stack</strong> o sube código al repositorio Git para agregar stacks manualmente.',
    },
    maintainer: {
      description: 'Designar a un equipo como mantenedor del stack. Nota: Este atributo es solo para fines informativos.',
      label: 'Mantenedor',
      noMaintainer: 'Sin mantenedor',
      assignMaintainer: 'Asignar responsable',
    },
    tags: '@:untranslated.tags',
    stackActions: 'Acciones de Stack',
    version: {
      title: 'Versión',
      branchBasedVersioning: 'Versionado basado en ramas.',
      branchBasedDescription: 'Use ramas para probar cambios en un entorno de desarrollo antes de publicar una versión estable.',
      currentVersion: 'Actualmente estás usando la versión <i>{versionName}</i>',
      updateAvailable: 'Actualización disponible.',
      updateAvailableDescription: 'Este componente fue aprovisionado usando la versión del stack {currentVersion}. Una nueva versión del stack {newVersion} está ahora disponible.',
      updateDeprecatedVersion: 'Actualizar versión obsoleta.',
      updateDeprecatedDescription: 'Este componente fue aprovisionado usando la versión del stack {currentVersion} que está marcada como obsoleta. Una nueva versión del stack {newVersion} está ahora disponible.',
      newVersionAvailable: 'Hay una nueva versión del stack disponible',
      reviewUpdate: 'Revisar versión',
      selectVersionText: 'Selecciona la versión del stack que se usará para aprovisionar este componente.',
      stackVersion: 'Versión del stack',
      targetVersion: 'Versión objetivo',
      upToDate: {
        title: 'Componente actualizado',
        branchText: 'Este componente usa la rama {versionName} en el commit {commitHash} de {stackCanonical}.',
        tagText: 'Este componente usa la etiqueta {versionName} en el commit {commitHash} de {stackCanonical}.',
      },
      deletedVersionWarning: 'La versión que usaba este componente ya no está disponible. Por favor revisa la configuración y selecciona una nueva versión antes de guardar.',
      refreshVersions: 'Actualizar versiones',
      refreshVersionsSharedStackTooltip: 'Este stack pertenece a otra organización. Solo ellos pueden actualizar las versiones.',
      changeVersion: 'Cambiar versión',
      outOfSyncBranch: {
        title: 'Componente desincronizado',
        text: 'Este componente no coincide con el último commit de la rama {versionName} de {stackCanonical}.',
        current: 'Actual:',
        latest: 'Último:',
        instructions: 'Revise y aplique la actualización para volver a sincronizar el componente.',
      },
      outOfSyncTag: {
        title: 'Componente desincronizado',
        text: 'Este componente no coincide con el commit referenciado actualmente por la etiqueta {versionName} de {stackCanonical}.',
        current: 'Actual:',
        referencedCommit: 'Commit referenciado:',
        instructions: 'Revise y aplique la actualización para volver a sincronizar el componente.',
      },
      newStackVersionAvailable: {
        title: 'Nueva versión del stack disponible',
        text: 'La última versión de {stackCanonical} es {latestVersion}, usted está usando {currentVersion}.',
      },
      deleted: {
        title: 'Versión del stack eliminada',
        text: 'La versión del stack {currentVersion} utilizada por este componente ya no existe en {stackCanonical}.',
        instructions: 'Actualice este componente y seleccione una versión válida.',
      },
    },
    useCase: {
      placeholder: 'Selecciona un caso de uso',
      description: 'Cada caso de uso expone una configuración diferente.',
      hint: 'Selecciona un caso de uso para configurar este componente',
      info: 'Define la variante funcional del stack. Esto no se puede cambiar después de la creación.',
      originalUseCaseUnavailableWithTeam: '<b>Caso de uso original no disponible.</b> El caso de uso configurado <i>{usecase}</i> no existe en la versión del stack seleccionada. Por favor, contacte al equipo <i>{team}</i> (mantenedores del stack) para resolver la incompatibilidad.',
      originalUseCaseUnavailableWithAdmin: '<b>Caso de uso original no disponible.</b> El caso de uso configurado <i>{usecase}</i> no existe en la versión del stack seleccionada. Por favor, contacte a un administrador para resolver la incompatibilidad.',
    },
    visibility: {
      title: 'Acceso y responsabilidad',
      description: 'Elija una visibilidad y asigne mantenedores a los stacks ya presentes en el repositorio de git. Siempre puede cambiar esto más adelante para cada stack.',
      label: 'Visibilidad del stack',
      local: 'Local',
      localHint: 'Sólo accesible para usuarios dentro de esta organización.',
      shared: 'Compartido',
      sharedHint: 'Accesible para los usuarios dentro de esta organización y sus organizaciones secundarias.',
      sharedByParentOrgHint: 'Compartido por una organización principal. Accesible para los usuarios de esta organización y de todas sus organizaciones secundarias.',
      hidden: 'Oculto',
      hiddenHint: 'No se muestra en la lista de stacks ni durante la creación del proyecto. Los usuarios de esta organización pueden seguir usándola revelando manualmente los stacks ocultos.',
      changeVisibility: 'Cambiar visibilidad',
      set: 'Establecer visibilidad',
    },
    outdated: 'desactualizada',
    outOfSync: {
      text: 'desincronizado',
      title: 'Este componente está desincronizado',
      description: 'El commit para <b>{stackName} {version}</b> ha cambiado, probablemente debido a una actualización en la etiqueta o rama asociada.',
      instructions: 'Actualiza este componente para alinearlo con el último commit.',
      action: 'Revisar actualización',
    },
  },
  fr: {
    blueprint: 'Modèle',
    branches: 'Branches',
    aboutBranch: {
      title: 'À propos de cette branche.',
      content: `La branche sélectionnée est celle que Cycloid utilise pour détecter les stacks dans le dépôt. Une fois le référentiel de catalogue créée, vous pourrez créer des branches supplémentaires pour tester de nouvelles versions de vos stacks.`,
    },
    catalogStatus: {
      allHint: 'Stacks publiques et privées',
      ownHint: 'Toutes les stacks de votre organisation',
      ownName: `Stacks d'organisation`,
    },
    cloneInstructions: 'Clonez le dépot de catalogue sur votre machine locale pour copier ou modifier des stacks. Ensuite, validez et transférez vos modifications vers votre dépot de catalogue cible.',
    gitRepo: 'Dépôt git',
    gitRepoHint: 'Connectez un dépôt git existant.',
    changeStackVersion: 'Changer la version du stack',
    clone: 'Cloner',
    commit: 'Commit',
    deleted: 'deleted',
    incompatible: {
      tag: 'Incompatible',
      title: 'Stack incompatible',
      alreadyUsingV3: `Cet environnement a déjà une stack utilisant le <b>format de la version 3</b>, il ne peut y avoir qu'une stack utilisant le format de la version 1-3 dans un même environnement.`,
      migrateToV4: 'Pour ajouter cette stack, vous devrez la migrer vers la <b>version 4</b>.',
      docsLink: 'Voir le guide de migration vers le format de stack v4',
    },
    latest: 'plus récente',
    noStacksFound: {
      title: 'Aucune stack trouvée',
      subtitle: 'Commençons par créer une stack dans ce dépôt. Cliquez sur le bouton <strong>Créer une nouvelle stack</strong> ou poussez du code vers le dépôt Git pour ajouter des stacks manuellement.',
    },
    maintainer: {
      description: 'Désigner une équipe comme responsable de la stack. Remarque : Cet attribut est uniquement à des fins informatives.',
      label: 'Mainteneur',
      noMaintainer: 'Pas de mainteneur',
      assignMaintainer: 'Assigner un responsable',
    },
    tags: '@:untranslated.tags',
    stackActions: 'Actions de stack',
    version: {
      title: 'Version',
      branchBasedVersioning: 'Versionnage basé sur les branches.',
      branchBasedDescription: 'Utilisez des branches pour tester les modifications dans un environnement de développement avant de publier une version stable.',
      currentVersion: 'Vous utilisez actuellement la version <i>{versionName}</i>',
      updateAvailable: 'Mise à jour disponible.',
      updateAvailableDescription: 'Ce composant a été provisionné en utilisant la version de la stack {currentVersion}. Une nouvelle version de la stack {newVersion} est maintenant disponible.',
      updateDeprecatedVersion: 'Mettre à jour la version obsolète.',
      updateDeprecatedDescription: 'Ce composant a été provisionné en utilisant la version de la stack {currentVersion} qui est marquée comme obsolète. Une nouvelle version de la stack {newVersion} est maintenant disponible.',
      newVersionAvailable: 'Une nouvelle version de la stack est disponible',
      reviewUpdate: 'Voir la mise à jour',
      selectVersionText: 'Sélectionnez la version de la stack qui sera utilisée pour approvisionner ce composant.',
      stackVersion: 'Version de la stack',
      targetVersion: 'Version cible',
      upToDate: {
        title: 'Composant à jour',
        branchText: 'Ce composant utilise la branche {versionName} sur le commit {commitHash} de {stackCanonical}.',
        tagText: 'Ce composant utilise le tag {versionName} sur le commit {commitHash} de {stackCanonical}.',
      },
      deletedVersionWarning: `La version utilisée par ce composant n'est plus disponible. Veuillez revoir la configuration et sélectionner une nouvelle version avant d'enregistrer.`,
      refreshVersions: 'Actualiser les versions',
      refreshVersionsSharedStackTooltip: 'Cette stack appartient à une autre organisation. Seuls eux peuvent actualiser les versions.',
      changeVersion: 'Changer de version',
      outOfSyncBranch: {
        title: 'Composant désynchronisé',
        text: 'Ce composant ne correspond pas au dernier commit de la branche {versionName} de {stackCanonical}.',
        current: 'Actuel:',
        latest: 'Dernier:',
        instructions: 'Examinez et appliquez la mise à jour pour resynchroniser le composant.',
      },
      outOfSyncTag: {
        title: 'Composant désynchronisé',
        text: 'Ce composant ne correspond pas au commit actuellement référencé par le tag {versionName} de {stackCanonical}.',
        current: 'Actuel:',
        referencedCommit: 'Commit référencé:',
        instructions: 'Examinez et appliquez la mise à jour pour resynchroniser le composant.',
      },
      newStackVersionAvailable: {
        title: 'Nouvelle version de la stack disponible',
        text: 'La dernière version de {stackCanonical} est {latestVersion}, vous utilisez {currentVersion}.',
      },
      deleted: {
        title: 'Version du stack supprimée',
        text: 'La version du stack {currentVersion} utilisée par ce composant n\'existe plus dans {stackCanonical}.',
        instructions: 'Mettez à jour ce composant et sélectionnez une version valide.',
      },
    },
    useCase: {
      placeholder: `Sélectionnez un cas d'utilisation`,
      description: `Chaque cas d'utilisation expose une configuration différente.`,
      hint: `Sélectionnez un cas d'utilisation pour configurer ce composant`,
      info: 'Définit la variante fonctionnelle du stack. Cela ne peut pas être modifié après la création.',
      originalUseCaseUnavailableWithTeam: `<b>Cas d'utilisation original indisponible.</b> Le cas d'utilisation configuré <i>{usecase}</i> n'existe pas dans la version de stack sélectionnée. Veuillez contacter l'équipe <i>{team}</i> (mainteneurs du stack) pour résoudre l'incompatibilité.`,
      originalUseCaseUnavailableWithAdmin: `<b>Cas d'utilisation original indisponible.</b> Le cas d'utilisation configuré <i>{usecase}</i> n'existe pas dans la version de stack sélectionnée. Veuillez contacter un administrateur pour résoudre l'incompatibilité.`,
    },
    visibility: {
      title: 'Accès et responsabilité',
      description: 'Choisissez une visibilité et assignez des mainteneurs aux stacks déjà présentes dans le dépôt git. Vous pouvez toujours changer cela plus tard pour chaque stack.',
      label: 'Visibilité de la stack',
      local: 'Local',
      localHint: 'Accessible uniquement aux utilisateurs de cette organisation.',
      shared: 'Partagé',
      sharedHint: 'Accessible aux utilisateurs de cette organisation et de ses organisations enfants.',
      sharedByParentOrgHint: 'Partagé par une organisation parente. Accessible aux utilisateurs de cette organisation et de toutes ses organisations enfants.',
      hidden: 'Caché',
      hiddenHint: `Non affiché dans la liste des stacks ou lors de la création du projet. Les utilisateurs de cette organisation peuvent toujours l'utiliser en révélant manuellement les stacks masquées.`,
      changeVisibility: 'Changer la visibilité',
      set: 'Définir la visibilité',
    },
    outdated: 'désactualisée',
    outOfSync: {
      text: 'désynchronisé',
      title: 'Ce composant est désynchronisé',
      description: 'Le commit pour <b>{stackName} {version}</b> a changé, probablement en raison d\'une mise à jour de la balise ou de la branche associée.',
      instructions: 'Mettez à jour ce composant pour l\'aligner avec le dernier commit.',
      action: 'Examiner la mise à jour',
    },
  },
}
